import { useState, type FormEvent } from 'react'
import { Link } from 'react-router'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { CommandProposal, InterpretCommandResponse } from '@ousse/shared'
import { api } from '../lib/api'
import { useHousehold } from '../lib/household'
import { Alert, ErrorAlert, Field } from './ui'

function proposalLabel(
  t: (k: string, o?: Record<string, string>) => string,
  p: CommandProposal,
): string {
  if (p.type === 'find_item') return t('commands.proposeFind', { item: p.item.name })
  if (p.type === 'move_item') {
    return t('commands.proposeMove', { item: p.item.name, location: p.location.name })
  }
  if (p.type === 'lend_item') {
    return t('commands.proposeLend', { item: p.item.name, borrower: p.borrower })
  }
  return t('commands.proposeReturn', { item: p.item.name })
}

/** Champ de commande rapide (accueil) : interpréter → confirmer → appliquer. */
export function QuickCommand() {
  const { t } = useTranslation()
  const { current } = useHousehold()
  const [text, setText] = useState('')
  const [error, setError] = useState<unknown>(null)
  const [result, setResult] = useState<InterpretCommandResponse | null>(null)
  const [done, setDone] = useState<{ message: string; itemId?: string } | null>(null)
  const hid = current?.id
  const canWrite = Boolean(current && current.role !== 'viewer' && !current.readOnly)

  const interpret = useMutation({
    mutationFn: (value: string) =>
      api.post<InterpretCommandResponse>(`/households/${hid}/commands/interpret`, { text: value }),
    onSuccess: (data) => {
      setError(null)
      setDone(null)
      setResult(data)
    },
    onError: setError,
  })

  const apply = useMutation({
    mutationFn: (proposal: CommandProposal) =>
      api.post<{ ok: true; path?: { name: string }[]; itemId?: string; locationId?: string | null }>(
        `/households/${hid}/commands/apply`,
        { proposal },
      ),
    onSuccess: (data, proposal) => {
      setError(null)
      setResult(null)
      setText('')
      if (proposal.type === 'find_item') {
        const path =
          data.path && data.path.length > 0
            ? data.path.map((p) => p.name).join(' › ')
            : null
        setDone({
          message: path
            ? t('commands.findResult', { name: proposal.item.name, path })
            : t('commands.findUnsorted', { name: proposal.item.name }),
          itemId: data.itemId,
        })
        return
      }
      setDone({ message: t('commands.done'), itemId: data.itemId })
    },
    onError: setError,
  })

  if (!hid) return null

  return (
    <section className="card stack" aria-labelledby="quick-command-heading">
      <h2 id="quick-command-heading">{t('home.commandLabel')}</h2>
      <p className="muted">{t('home.commandHint')}</p>
      <form
        className="stack"
        onSubmit={(e: FormEvent) => {
          e.preventDefault()
          const v = text.trim()
          if (!v) return
          interpret.mutate(v)
        }}
      >
        <Field
          label={t('home.commandLabel')}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t('home.commandPlaceholder')}
          maxLength={500}
        />
        <button type="submit" className="btn" disabled={interpret.isPending || !text.trim()}>
          {t('home.commandSubmit')}
        </button>
      </form>
      <ErrorAlert error={error} />
      {done && (
        <Alert kind="success">
          <p>{done.message}</p>
          {done.itemId && (
            <Link className="btn secondary" to={`/items/${done.itemId}`}>
              {t('commands.openItem')}
            </Link>
          )}
        </Alert>
      )}
      {result && result.status === 'unsupported' && (
        <Alert kind="warning">{t(result.reason ?? 'commands.unsupported')}</Alert>
      )}
      {result && (result.status === 'unknown' || result.status === 'ambiguous') && (
        <Alert kind="warning">
          <p>{t(result.reason ?? 'commands.ambiguous')}</p>
          {result.alternatives && result.alternatives.length > 0 && (
            <ul className="list">
              {result.alternatives.map((alt, i) => (
                <li key={i}>
                  <button
                    type="button"
                    className="btn secondary"
                    disabled={!canWrite && alt.type !== 'find_item'}
                    onClick={() => apply.mutate(alt)}
                  >
                    {t('commands.pickAlternative')} : {proposalLabel(t, alt)}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Alert>
      )}
      {result?.status === 'ok' && result.proposal && (
        <Alert kind="warning">
          <p>
            <strong>{t('commands.confirmTitle')}</strong>
            <br />
            {proposalLabel(t, result.proposal)}
          </p>
          <button
            type="button"
            className="btn"
            disabled={apply.isPending || (!canWrite && result.proposal.type !== 'find_item')}
            onClick={() => apply.mutate(result.proposal!)}
          >
            {t('commands.apply')}
          </button>
          <button type="button" className="btn ghost" onClick={() => setResult(null)}>
            {t('common.cancel')}
          </button>
        </Alert>
      )}
    </section>
  )
}
